import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthConditional } from "@/lib/use-auth-conditional";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

export interface Milestone {
  id: string;
  projectId: string;
  name: string;
  targetDate: string | null;
  completedAt: string | null;
  status: "planned" | "in_progress" | "completed" | "skipped" | null;
  effectiveStatus: "planned" | "in_progress" | "completed" | "skipped";
  sortOrder: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface MilestoneTemplate {
  type: string;
  label: string;
  phases: string[];
}

/**
 * Fetch milestones for a project
 */
export function useMilestones(projectId: string) {
  const { getToken } = useAuthConditional();

  return useQuery({
    queryKey: ["milestones", projectId],
    queryFn: async (): Promise<{ milestones: Milestone[] }> => {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/projects/${projectId}/milestones`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch milestones");
      }

      return res.json();
    },
    enabled: !!projectId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch milestone templates
 */
export function useMilestoneTemplates() {
  const { getToken } = useAuthConditional();

  return useQuery({
    queryKey: ["milestone-templates"],
    queryFn: async (): Promise<{ templates: MilestoneTemplate[] }> => {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/milestone-templates`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch milestone templates");
      }

      return res.json();
    },
    staleTime: 300000, // 5 minutes - templates don't change often
  });
}

/**
 * Create a new milestone
 */
export function useCreateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthConditional();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      targetDate?: string;
      status?: "planned" | "in_progress" | "completed" | "skipped";
      notes?: string;
    }) => {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/projects/${projectId}/milestones`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to create milestone");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
}

/**
 * Update a milestone
 */
export function useUpdateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthConditional();

  return useMutation({
    mutationFn: async ({
      milestoneId,
      data,
    }: {
      milestoneId: string;
      data: {
        name?: string;
        targetDate?: string | null;
        completedAt?: string | null;
        status?: "planned" | "in_progress" | "completed" | "skipped" | null;
        sortOrder?: number;
        notes?: string | null;
      };
    }) => {
      const token = await getToken();
      const res = await fetch(
        `${API_URL}/api/projects/${projectId}/milestones/${milestoneId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(data),
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to update milestone");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
}

/**
 * Delete a milestone
 */
export function useDeleteMilestone(projectId: string) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthConditional();

  return useMutation({
    mutationFn: async (milestoneId: string) => {
      const token = await getToken();
      const res = await fetch(
        `${API_URL}/api/projects/${projectId}/milestones/${milestoneId}`,
        {
          method: "DELETE",
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to delete milestone");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
}

/**
 * Regenerate milestones from template
 */
export function useRegenerateMilestones(projectId: string) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthConditional();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const res = await fetch(
        `${API_URL}/api/projects/${projectId}/milestones/regenerate`,
        {
          method: "POST",
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to regenerate milestones");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
}

/**
 * Reorder milestones
 */
export function useReorderMilestones(projectId: string) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthConditional();

  return useMutation({
    mutationFn: async (milestoneIds: string[]) => {
      const token = await getToken();
      const res = await fetch(
        `${API_URL}/api/projects/${projectId}/milestones/reorder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ milestoneIds }),
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to reorder milestones");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
}
